import { Alert, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { CancellationPenaltyFields, isPenaltyReconfirmed } from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

export function PenaltyReconfirmation({ booking, compact = false }: {
  booking: CancellationPenaltyFields & { id: string; state?: string };
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const path = `booking-follow-up/bookings/${booking.id}/reconfirmation`;
  const key = [
    'penalty-reconfirmation',
    booking.id,
    booking.cancellationPenaltyTiming,
    booking.cancellationPenaltyStartsAt,
  ];
  const query = useQuery(
    key,
    async () => (await client.get<CancellationPenaltyFields & { state: string }>(path)).data,
    { enabled: !compact && booking.cancellationPenaltyTiming === 'scheduled', retry: false },
  );
  // Rows already contain these fields. Avoid one extra request per booking.
  const current = compact ? booking : query.data;
  // Only reconfirm the deadline shown by the parent booking detail, never a newer unseen one.
  const sameDeadline = current?.cancellationPenaltyStartsAt === booking.cancellationPenaltyStartsAt ||
    (!!current?.cancellationPenaltyStartsAt && !!booking.cancellationPenaltyStartsAt &&
      Date.parse(current.cancellationPenaltyStartsAt) === Date.parse(booking.cancellationPenaltyStartsAt));
  const stale =
    !!current &&
    (current.cancellationPenaltyTiming !== booking.cancellationPenaltyTiming ||
      !sameDeadline);
  const mutation = useMutation(
    async (reconfirmed: boolean) => {
      await client.put(path, { reconfirmed, deadline: booking.cancellationPenaltyStartsAt });
    },
    {
      onError: async () => {
        await cache.invalidateQueries(key);
        await cache.invalidateQueries([QueryKeys.getTravelBookings]);
      },
      onSuccess: async () => {
        await Promise.all([
          cache.invalidateQueries(key),
          cache.invalidateQueries([QueryKeys.getTravelBookings]),
          cache.invalidateQueries(['hotel-reservation']),
        ]);
      },
    },
  );
  const hasDeadline = booking.cancellationPenaltyTiming === 'scheduled' && !!booking.cancellationPenaltyStartsAt;
  const reason = !hasDeadline ? 'noDeadline'
    : !current ? 'loading'
      : !['held', 'confirmed'].includes(current.state ?? '') ? 'inactiveBooking'
        : null;
  return (
    <Stack onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
      <FormControlLabel
        label={t('penaltyReminders.reconfirmed')}
        control={
          <Checkbox
            checked={!stale && !!current && isPenaltyReconfirmed(current)}
            disabled={
              !current ||
              !hasDeadline ||
              stale ||
              query.isError ||
              query.isFetching ||
              mutation.isLoading ||
              !['held', 'confirmed'].includes(current.state ?? '') ||
              current.cancellationPenaltyTiming !== 'scheduled' ||
              !current.cancellationPenaltyStartsAt
            }
            onChange={(_, checked) => mutation.mutate(checked)}
          />
        }
      />
      {reason ? <Typography variant="caption" color="text.secondary">{t(`penaltyReminders.${reason}`)}</Typography>
        : !compact && <Typography variant="caption">{t('penaltyReminders.reconfirmationHint')}</Typography>}
      {!stale && current && isPenaltyReconfirmed(current) && (
        <Typography variant="caption">
          {t('penaltyReminders.savedAt', { date: new Date(current.penaltyReconfirmedAt!).toLocaleString() })}
        </Typography>
      )}
      {(stale || query.isError || mutation.isError) && <Alert severity="error">{t('penaltyReminders.error')}</Alert>}
    </Stack>
  );
}
